import { db } from "../firebase/config";
import { collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot, query, where, getDocs } from "firebase/firestore";

// Listen to all vendors in real-time
export function subscribeToVendors(callback) {
  return onSnapshot(collection(db, "vendors"), (snapshot) => {
    const list = [];
    snapshot.forEach((doc) => {
      list.push({ id: doc.id, ...doc.data() });
    });
    // Sort alphabetically by name
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    callback(list);
  }, (error) => {
    console.error("Error subscribing to vendors:", error);
    callback([], error);
  });
}

// Ensure default vendors are seeded if the collection is empty
export async function seedDefaultVendorsIfEmpty(vendors) {
  if (vendors.length > 0) return;

  const mockVendors = [
    { 
      name: "Atlas Shipping Co.", 
      category: "Logistics", 
      status: "Active", 
      tags: ["Services"],
      address: "123 Port Road",
      city: "Seattle",
      state: "WA",
      zip: "98101",
      pocs: [{ name: "Sarah Connor", title: "Operations Manager", email: "sarah@atlasshipping.com", phone: "555-0192" }]
    },
    { 
      name: "Apex Manufacturing Ltd.", 
      category: "Manufacturing", 
      status: "Active", 
      tags: ["Steel", "Hardware"],
      address: "456 Factory Lane",
      city: "Chicago",
      state: "IL",
      zip: "60609",
      pocs: [{ name: "James Miller", title: "Plant Supervisor", email: "j.miller@apexmfg.com", phone: "555-0283" }]
    },
    { 
      name: "Global Warehousing Corp.", 
      category: "Warehousing", 
      status: "Active", 
      tags: ["Packaging"],
      address: "789 Logistics Way",
      city: "Dallas",
      state: "TX",
      zip: "75201",
      pocs: [{ name: "Elena Rostova", title: "Warehouse Director", email: "e.rostova@globalwh.com", phone: "555-0144" }]
    },
    { 
      name: "Pacific Custom Brokerage", 
      category: "Customs", 
      status: "Active", 
      tags: ["Services"],
      address: "101 Border Blvd",
      city: "San Diego",
      state: "CA",
      zip: "92101",
      pocs: [{ name: "David Kim", title: "Customs Agent", email: "david.kim@paccustoms.com", phone: "555-0921" }]
    },
    { 
      name: "Vanguard Materials Inc.", 
      category: "Raw Materials", 
      status: "Inactive", 
      tags: ["Chemicals", "Steel"],
      address: "202 Refinery Road",
      city: "Houston",
      state: "TX",
      zip: "77002",
      pocs: [{ name: "Marcus Vance", title: "Procurement Manager", email: "m.vance@vanguard.com", phone: "555-0371" }]
    }
  ];

  for (const item of mockVendors) {
    try {
      await addDoc(collection(db, "vendors"), item);
    } catch (e) {
      console.error("Failed to seed vendor:", e);
    }
  }
}

// Add a new vendor
export async function addVendor(vendorData) {
  const docRef = await addDoc(collection(db, "vendors"), vendorData);
  return docRef.id;
}

// Update an existing vendor
export async function updateVendor(id, vendorData) {
  const docRef = doc(db, "vendors", id);
  await updateDoc(docRef, vendorData);
}

// Delete a vendor
export async function deleteVendor(id) {
  const docRef = doc(db, "vendors", id);
  await deleteDoc(docRef);
}

// Listen to vendor categories
export function subscribeToVendorCategories(callback) {
  return onSnapshot(collection(db, "vendor_categories"), (snapshot) => {
    let list = [];
    snapshot.forEach((doc) => {
      list.push(doc.data().name);
    });
    if (list.length === 0) {
      callback(["Logistics", "Procurement", "Warehousing", "Manufacturing", "Raw Materials", "Customs"]);
    } else {
      list.sort((a, b) => a.localeCompare(b));
      callback(list);
    }
  }, (error) => {
    console.error("Error subscribing to vendor categories:", error);
    callback([], error);
  });
}

// Listen to product tags
export function subscribeToProductTags(callback) {
  return onSnapshot(collection(db, "product_tags"), (snapshot) => {
    let list = [];
    snapshot.forEach((doc) => {
      list.push(doc.data().name);
    });
    if (list.length === 0) {
      callback(["Electronics", "Steel", "Packaging", "Fasteners", "Chemicals", "Hardware", "Services"]);
    } else {
      list.sort((a, b) => a.localeCompare(b));
      callback(list);
    }
  }, (error) => {
    console.error("Error subscribing to product tags:", error);
    callback([], error);
  });
}

// Listen to brand tags
export function subscribeToBrandTags(callback) {
  return onSnapshot(collection(db, "brand_tags"), (snapshot) => {
    let list = [];
    snapshot.forEach((doc) => {
      list.push(doc.data().name);
    });
    list.sort((a, b) => a.localeCompare(b));
    callback(list);
  }, (error) => {
    console.error("Error subscribing to brand tags:", error);
    callback([], error);
  });
}

// Add a brand tag
export async function addBrandTag(name) {
  const docRef = await addDoc(collection(db, "brand_tags"), { name: name.trim() });
  return docRef.id;
}

// Update a brand tag and cascade to vendors
export async function updateBrandTag(id, newName, oldName) {
  await updateDoc(doc(db, "brand_tags", id), { name: newName.trim() });
  if (oldName && oldName !== newName) {
    const q = query(collection(db, "vendors"), where("brandTags", "array-contains", oldName));
    const querySnapshot = await getDocs(q);
    for (const vendorDoc of querySnapshot.docs) {
      const vendorData = vendorDoc.data();
      const currentBrandTags = vendorData.brandTags || [];
      const updatedBrandTags = currentBrandTags.map(t => t === oldName ? newName.trim() : t);
      await updateDoc(doc(db, "vendors", vendorDoc.id), { brandTags: updatedBrandTags });
    }
  }
}

// Delete a brand tag and cascade to vendors
export async function deleteBrandTag(id, name) {
  await deleteDoc(doc(db, "brand_tags", id));
  if (name) {
    const q = query(collection(db, "vendors"), where("brandTags", "array-contains", name));
    const querySnapshot = await getDocs(q);
    for (const vendorDoc of querySnapshot.docs) {
      const vendorData = vendorDoc.data();
      const currentBrandTags = vendorData.brandTags || [];
      const updatedBrandTags = currentBrandTags.filter(t => t !== name);
      await updateDoc(doc(db, "vendors", vendorDoc.id), { brandTags: updatedBrandTags });
    }
  }
}

